// PendingReqListing.jsx
// Connects to Node backend:
//   GET  /api/PR_getBranchDropdown                → branch dropdown
//   GET  /api/getPendingPurchaseReturnDetails     → pending request grid
//   GET  /api/getPendingPurchaseRequestDetailsByID → selected request item details
//
// Row select   → loads request item details
// Item select  → navigate to PurchaseDetails with { requestId, itemId }

import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import {
  Search,
  Filter,
  List,
  CheckCircle,
  X,
  Eye,
  ClipboardList,
  Building2,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "https://nissi-india.com/api";

/* ─── Helpers ───────────────────────────────────────────────────────────── */
// yyyy-MM-dd  →  dd/MM/yyyy
const toApiDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// dd/MM/yyyy  →  yyyy-MM-dd
const toInputDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-")) return dateStr;
  const [d, m, y] = dateStr.split("/");
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const PendingReqListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ── session / localStorage ─────────────────────────────── */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdLS = localStorage.getItem("branchId") || "8";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const storedBranchId = parseInt(branchIdLS || "8", 10);
  const initialBranchType =
    branch === "HO" || branch === "HEAD-OFFICE" ? "HO" : "Branch";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── filter state ───────────────────────────────────────── */
  const [branchType, setBranchType] = useState(initialBranchType);
  const [branchId, setBranchId] = useState(storedBranchId);
  const [branchList, setBranchList] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ── ui state ───────────────────────────────────────────── */
  const [data, setData] = useState([]);
  const [requestDetails, setRequestDetails] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  /* ─────────────────────────────────────────────────────────
     1. INIT — load branch dropdown
  ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const loadBranches = async () => {
      setInitLoading(true);

      try {
        const res = await axios.get(`${BASE_URL}/PR_getBranchDropdown`, {
          params: {
            branchtype: branchType,
            branchid: storedBranchId,
          },
          headers: {
            ...commonHeaders,
            "x-branchtype": branchType,
          },
        });

        if (!res.data.success) throw new Error(res.data.message);

        const branches = res.data.branches || [];
        setBranchList(branches);

        if (res.data.selectedBranch) {
          setBranchId(res.data.selectedBranch);
        } else if (branches.length > 0) {
          setBranchId(branches[0].fld_branchid);
        }
      } catch (err) {
        console.error("Branch dropdown fetch failed:", err);
        toast.error("Failed to load branches");
      } finally {
        setInitLoading(false);
      }
    };

    loadBranches();
  }, [branchType, storedBranchId, commonHeaders]);

  /* ─────────────────────────────────────────────────────────
     2. BRANCH TYPE CHANGE
  ───────────────────────────────────────────────────────── */
  const handleBranchTypeChange = (val) => {
    setBranchType(val);
    setBranchId(storedBranchId);
    setData([]);
    setRequestDetails([]);
    setSelectedRequestId(null);
  };

  /* ─────────────────────────────────────────────────────────
     3. SEARCH
  ───────────────────────────────────────────────────────── */
  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    setLoading(true);
    setData([]);
    setRequestDetails([]);
    setSelectedRequestId(null);

    try {
      const res = await axios.get(`${BASE_URL}/getPendingPurchaseReturnDetails`, {
        params: {
          fromdate: toApiDate(fromDate),
          todate: toApiDate(toDate),
          branchid: branchId,
          req_type: branchType === "HO" ? "H" : "B",
          search: "",
          page: 1,
          limit: 200,
        },
        headers: {
          ...commonHeaders,
          "x-branchtype": branchType,
        },
      });

      if (!res.data.success) throw new Error(res.data.message);

      const rows = res.data.rows || [];
      setData(rows);

      if (rows.length === 0) {
        toast.info("No records found for selected filters");
      }
    } catch (err) {
      console.error("Search failed:", err);
      toast.error(err.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     4. LOAD REQUEST ITEM DETAILS
  ───────────────────────────────────────────────────────── */
  const handleSelectRequest = async (requestId) => {
    setSelectedRequestId(requestId);
    setRequestDetails([]);
    setDetailsLoading(true);

    try {
      const res = await axios.get(
        `${BASE_URL}/getPendingPurchaseRequestDetailsByID`,
        {
          params: { requestid: requestId },
          headers: {
            ...commonHeaders,
            "x-branchtype": branchType,
          },
        }
      );

      if (!res.data.success) throw new Error(res.data.message);

      const rows = res.data.rows || [];
      setRequestDetails(rows);

      if (rows.length === 0) {
        toast.info("No item details found for selected request");
      }
    } catch (err) {
      console.error("Failed to fetch purchase request details:", err);
      toast.error(
        err.response?.data?.message || "Failed to load request item details"
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     5. ITEM SELECT
  ───────────────────────────────────────────────────────── */
  const handleItemSelect = (itemId, requestId) => {
    navigate("/inventory/PurchaseDetails", {
      state: {
        requestId,
        itemId,
      },
    });
  };

  /* ─────────────────────────────────────────────────────────
     6. CLEAR FILTERS
  ───────────────────────────────────────────────────────── */
  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setBranchId(storedBranchId);
    setData([]);
    setRequestDetails([]);
    setSelectedRequestId(null);
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Pending Purchase Request Listing
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Pending Purchase Requests</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Search and select pending purchase requests
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.length} records
              </span>
            </div>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
            >
              {showFilters ? <X size={16} /> : <Filter size={16} />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Branch Type Toggle */}
          <div>
            <label className={labelClass}>Branch Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleBranchTypeChange("HO")}
                disabled={branch !== "HO" && branch !== "HEAD-OFFICE"}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white shadow`
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${
                  branch !== "HO" && branch !== "HEAD-OFFICE"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <Building2 size={16} />
                H.O
              </button>

              <button
                type="button"
                onClick={() => handleBranchTypeChange("Branch")}
                disabled={branch === "HO" || branch === "HEAD-OFFICE"}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white shadow"
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${
                  branch === "HO" || branch === "HEAD-OFFICE"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              {initLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                  <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Loading filters…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <div>
                      <label className={labelClass}>
                        From Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={toInputDate(fromDate)}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        To Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={toInputDate(toDate)}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select</option>
                        {branchList.map((b) => (
                          <option key={b.fld_branchid} value={b.fld_branchid}>
                            {b.fld_branchname}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={clearFilters}
                      className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                        isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Clear Filters
                    </button>

                    <button
                      onClick={handleSearch}
                      disabled={loading}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                    >
                      {loading ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search size={16} />
                      )}
                      {loading ? "Searching…" : "Search Requests"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Grid — Purchase Requests */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <span className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.length > 0 ? (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <List size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Purchase Requests</h3>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    {[
                      "S.No",
                      "Requisition No",
                      "Date",
                      "Branch",
                      "Department",
                      "Remarks",
                      "Action",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {data.map((row, i) => (
                    <tr
                      key={row.fld_PurchaseRequestid || i}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      } ${
                        selectedRequestId === row.fld_PurchaseRequestid
                          ? isDark
                            ? `bg-${baseColor}-900/20`
                            : `bg-${baseColor}-50`
                          : ""
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-center">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {row.fld_PurchaseRequestNo || "—"}
                      </td>
                      <td className="px-4 py-3">{row.fld_date || "—"}</td>
                      <td className="px-4 py-3">{row.fld_branchname || "—"}</td>
                      <td className="px-4 py-3">
                        {row.fld_departmentname || "—"}
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate text-gray-500">
                        {row.fld_remarks || row.fld_Remarks || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            handleSelectRequest(row.fld_PurchaseRequestid)
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
                        >
                          <Eye size={14} />
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="flex flex-col items-center justify-center py-16">
              <ClipboardList size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Adjust your filters and search to find pending purchase requests.
              </p>
            </div>
          </div>
        )}

        {/* Details Grid — Items for Selected Request */}
        {detailsLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="w-6 h-6 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : requestDetails.length > 0 ? (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <CheckCircle size={20} className="text-green-600" />
                <div>
                  <h3 className="text-xl font-bold">
                    Items for Selected Request
                  </h3>
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Select an item to proceed to purchase details
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    {["S.No", "Item Name", "Item Code", "Action"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {requestDetails.map((row, i) => (
                    <tr
                      key={row.fld_requestitemid || i}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-center">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {row.fld_itemname || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {row.fld_itemcode || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            handleItemSelect(
                              row.fld_itemid,
                              row.fld_purchaserequestid
                            )
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
                        >
                          <CheckCircle size={14} />
                          Select Item
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : selectedRequestId ? (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="flex flex-col items-center justify-center py-16">
              <ClipboardList size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Item Details Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                This request does not contain any available item details.
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PendingReqListing;